import classNames from "classnames";
import React, {
  FormEvent,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { LuSearch, LuX } from "react-icons/lu";

import { Avatar } from "../../../components/newUI/avatar";
import { Button } from "../../../components/newUI/button";
import { Checkbox } from "../../../components/newUI/checkbox";
import { SelectOption } from "../../../components/newUI/select";

type Props = {
  defaultValue?: string;
  userOptions?: SelectOption[];
  defaultSelectedUsers?: SelectOption[];
  isLoading?: boolean;
  onClose: () => void;
  onSave: (roleName: string, selectedUserIds: string[]) => void;
};

const EMPTY_ROLE_USERS: SelectOption[] = [];
const MAX_SUMMARY_AVATARS = 10;

const LABEL_CLASSES = "mb-[7px] block text-[13px] font-bold text-[#293B5A]";
const INPUT_CLASSES =
  "w-full rounded-[9px] border border-[#DCE2EA] bg-white text-[#1F2433] outline-none transition-colors placeholder:text-[#9AA1AE] focus:border-[#6B4EFF] disabled:bg-[#F8F9FB]";
const AVATAR_CLASSES = "!h-7 !w-7 shrink-0 !text-[10.5px] !font-bold";

export const RoleModal: React.FC<Props> = ({
  onClose,
  defaultValue = "",
  userOptions,
  defaultSelectedUsers = EMPTY_ROLE_USERS,
  isLoading,
  onSave,
}) => {
  const [nameValue, setNameValue] = useState(defaultValue);
  const [searchValue, setSearchValue] = useState("");
  const [selectedUsers, setSelectedUsers] =
    useState<SelectOption[]>(defaultSelectedUsers);

  const trimmedName = nameValue.trim();
  const modalTitle = defaultValue ? "Edit role" : "Create role";
  const submitLabel = defaultValue ? "Save" : "Create role";
  const userSelectOptions = userOptions ?? EMPTY_ROLE_USERS;
  const normalizedSearch = searchValue.trim().toLowerCase();
  // Keep the member list as tall as it is without a search, so the window
  // doesn't shrink while typing.
  const memberListRef = useRef<HTMLDivElement>(null);
  const [memberListHeight, setMemberListHeight] = useState<number>();
  const visibleUserOptions = useMemo(() => {
    if (!normalizedSearch) {
      return userSelectOptions;
    }

    return userSelectOptions.filter((userOption) => {
      const label = userOption.label.toLowerCase();
      const caption = userOption.caption?.toLowerCase() ?? "";

      return (
        label.includes(normalizedSearch) || caption.includes(normalizedSearch)
      );
    });
  }, [normalizedSearch, userSelectOptions]);

  useLayoutEffect(() => {
    // Measure the full list (no search) so searching keeps the same height.
    if (!normalizedSearch && memberListRef.current) {
      setMemberListHeight(memberListRef.current.offsetHeight);
    }
  }, [normalizedSearch, userSelectOptions]);

  const selectedUserIds = useMemo(
    () => new Set(selectedUsers.map((selectedUser) => selectedUser.value)),
    [selectedUsers],
  );
  const areAllVisibleSelected =
    visibleUserOptions.length > 0 &&
    visibleUserOptions.every((userOption) =>
      selectedUserIds.has(userOption.value),
    );

  const toggleUser = (userOption: SelectOption) => {
    setSelectedUsers((currentUsers) =>
      selectedUserIds.has(userOption.value)
        ? currentUsers.filter(
            (currentUser) => currentUser.value !== userOption.value,
          )
        : [...currentUsers, userOption],
    );
  };

  const toggleAllVisible = () => {
    const visibleIds = new Set(
      visibleUserOptions.map((userOption) => userOption.value),
    );

    setSelectedUsers((currentUsers) =>
      areAllVisibleSelected
        ? currentUsers.filter(
            (currentUser) => !visibleIds.has(currentUser.value),
          )
        : [
            ...currentUsers,
            ...visibleUserOptions.filter(
              (userOption) => !selectedUserIds.has(userOption.value),
            ),
          ],
    );
  };

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (isLoading || !trimmedName) {
      return;
    }

    onSave(
      trimmedName,
      selectedUsers.map((selectedUser) => selectedUser.value),
    );
  };

  useEffect(() => {
    setSelectedUsers(defaultSelectedUsers);
  }, [defaultSelectedUsers]);

  useEffect(() => {
    setNameValue(defaultValue);
  }, [defaultValue]);

  const hiddenSummaryCount = selectedUsers.length - MAX_SUMMARY_AVATARS;

  return (
    <div className={"modal modal-open font-Manrope"}>
      <form
        onSubmit={handleSubmit}
        className={
          "modal-box flex max-h-[calc(100vh-48px)] w-[500px] max-w-[calc(100vw-32px)] flex-col overflow-hidden rounded-2xl p-0 shadow-[0_24px_70px_rgba(0,0,0,0.28)]"
        }>
        <div
          className={
            "flex items-start justify-between gap-4 border-b border-[#EEF0F3] px-7 pb-[18px] pt-6"
          }>
          <div>
            <div className={"text-xl font-extrabold text-[#1F2433]"}>
              {modalTitle}
            </div>
            <div className={"mt-1 text-[13px] leading-normal text-[#7A8194]"}>
              Name the role and add the team members who belong to it. Work
              assigned to this role sits in{" "}
              <span className={"font-semibold text-[#5B6478]"}>Queued</span>{" "}
              until someone picks it up.
            </div>
          </div>
          <button
            type={"button"}
            disabled={isLoading}
            aria-label={"Close role modal"}
            onClick={onClose}
            className={
              "flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-[#F1F2F5] text-[#6B7185] transition-colors hover:bg-[#E6E8EE] hover:text-[#1F2433] disabled:cursor-not-allowed"
            }>
            <LuX size={16} strokeWidth={2.4} />
          </button>
        </div>

        <div className={"min-h-0 flex-1 overflow-y-auto px-7 pb-6 pt-[22px]"}>
          <label htmlFor={"role-name"} className={LABEL_CLASSES}>
            Role name
          </label>
          <input
            id={"role-name"}
            type={"text"}
            className={classNames(
              INPUT_CLASSES,
              "mb-[22px] h-[43px] px-[13px] text-[14.5px]",
            )}
            value={nameValue}
            placeholder={"e.g. Bookkeeper, Tax preparer, Reviewer"}
            autoFocus
            disabled={isLoading}
            onChange={(event) => setNameValue(event.target.value)}
          />
          {!!userOptions && (
            <>
              <label htmlFor={"role-member-search"} className={LABEL_CLASSES}>
                Members
              </label>

              {selectedUsers.length > 0 && (
                <div
                  className={
                    "mb-3 flex h-[46px] items-center gap-2 rounded-[10px] border border-[#E6E0FF] bg-[#F6F4FF] px-[11px] py-2"
                  }>
                  <div className={"flex min-w-0 flex-1 items-center"}>
                    {selectedUsers
                      .slice(0, MAX_SUMMARY_AVATARS)
                      .map((selectedUser, index) => (
                        <div
                          key={selectedUser.value}
                          className={classNames(
                            "rounded-full border-2 border-[#F6F4FF]",
                            index > 0 && "-ml-1.5",
                          )}>
                          <Avatar
                            id={selectedUser.value}
                            label={selectedUser.avatar?.label}
                            fullName={selectedUser.label}
                            color={selectedUser.avatar?.color}
                            avatarClassNames={AVATAR_CLASSES}
                          />
                        </div>
                      ))}
                    {hiddenSummaryCount > 0 && (
                      <span
                        className={
                          "ml-2 text-[12.5px] font-semibold text-[#8A90A2]"
                        }>
                        +{hiddenSummaryCount}
                      </span>
                    )}
                  </div>
                  <button
                    type={"button"}
                    disabled={isLoading}
                    className={
                      "shrink-0 text-[12.5px] font-bold text-[#6B4EFF] hover:underline disabled:cursor-not-allowed"
                    }
                    onClick={() => setSelectedUsers([])}>
                    Remove all
                  </button>
                </div>
              )}

              <div className={"relative mb-3"}>
                <LuSearch
                  size={16}
                  className={
                    "pointer-events-none absolute left-[13px] top-1/2 -translate-y-1/2 text-[#8A90A2]"
                  }
                />
                <input
                  id={"role-member-search"}
                  type={"text"}
                  className={classNames(
                    INPUT_CLASSES,
                    "h-[41px] pl-9 pr-[38px] text-sm",
                  )}
                  value={searchValue}
                  placeholder={"Search team members..."}
                  disabled={isLoading}
                  onChange={(event) => setSearchValue(event.target.value)}
                />
                {searchValue && (
                  <button
                    type={"button"}
                    aria-label={"Clear search"}
                    className={
                      "absolute right-[10px] top-1/2 flex h-[22px] w-[22px] -translate-y-1/2 items-center justify-center rounded-full bg-[#EAECF1] text-[#6B7185] hover:bg-[#DCE0E8]"
                    }
                    onClick={() => setSearchValue("")}>
                    <LuX size={12} strokeWidth={2.6} />
                  </button>
                )}
              </div>

              <div
                className={
                  "overflow-hidden rounded-[11px] border border-[#ECEFF3]"
                }>
                <label
                  className={classNames(
                    "flex items-center gap-[9px] border-b border-[#ECEFF3] px-[13px] py-[10px]",
                    visibleUserOptions.length && !isLoading
                      ? "cursor-pointer"
                      : "cursor-not-allowed opacity-60",
                  )}>
                  <input
                    type={"checkbox"}
                    className={"sr-only"}
                    checked={areAllVisibleSelected}
                    disabled={!visibleUserOptions.length || isLoading}
                    onChange={toggleAllVisible}
                  />
                  <Checkbox
                    presentation
                    size={"md"}
                    checked={areAllVisibleSelected}
                  />
                  <span className={"text-[13px] font-bold text-[#6B4EFF]"}>
                    Select all
                  </span>
                  {selectedUsers.length > 0 && (
                    <span
                      className={
                        "ml-auto text-[12.5px] font-semibold text-[#8A90A2]"
                      }>
                      {selectedUsers.length} selected
                    </span>
                  )}
                </label>

                <div
                  ref={memberListRef}
                  className={"max-h-[264px] overflow-y-auto"}
                  style={
                    normalizedSearch && memberListHeight
                      ? { height: memberListHeight }
                      : undefined
                  }>
                  {visibleUserOptions.map((userOption) => {
                    const isSelected = selectedUserIds.has(userOption.value);

                    return (
                      <label
                        key={userOption.value}
                        className={classNames(
                          "flex cursor-pointer items-center gap-[11px] border-b border-[#F4F5F7] px-[13px] py-[10px] transition-colors last:border-b-0",
                          isSelected
                            ? "bg-[#F6F4FF]"
                            : "bg-white hover:bg-[#FAFAFC]",
                          isLoading && "pointer-events-none opacity-60",
                        )}>
                        <input
                          type={"checkbox"}
                          className={"sr-only"}
                          checked={isSelected}
                          disabled={isLoading}
                          onChange={() => toggleUser(userOption)}
                        />
                        <Checkbox
                          presentation
                          size={"md"}
                          checked={isSelected}
                        />
                        <Avatar
                          id={userOption.value}
                          label={userOption.avatar?.label}
                          fullName={userOption.label}
                          color={userOption.avatar?.color}
                          avatarClassNames={AVATAR_CLASSES}
                        />
                        <span
                          className={
                            "min-w-0 flex-1 truncate text-sm font-semibold text-[#1F2433]"
                          }>
                          {userOption.label}
                        </span>
                      </label>
                    );
                  })}

                  {!visibleUserOptions.length && (
                    <div
                      className={
                        "px-4 py-5 text-center text-[13px] text-[#8A90A2]"
                      }>
                      {normalizedSearch
                        ? `No team members match "${searchValue.trim()}"`
                        : "No team members found"}
                    </div>
                  )}
                </div>
              </div>
            </>
          )}
        </div>

        <div
          className={
            "flex flex-none items-center justify-end gap-3 border-t border-[#EEF0F3] px-7 py-4"
          }>
          <Button
            type={"button"}
            variant={"outline"}
            size={"md"}
            autoWidth
            disabled={isLoading}
            className={
              "!rounded-[9px] !border-[#DCE2EA] px-5 !text-sm !font-bold !text-[#3A4256]"
            }
            onClick={onClose}>
            Cancel
          </Button>

          <Button
            type={"submit"}
            size={"md"}
            autoWidth
            loading={isLoading}
            disabled={!trimmedName}
            className={
              "!h-[38px] !rounded-[9px] !border-[#6B4EFF] !bg-[#6B4EFF] px-[22px] !text-sm !font-bold shadow-[0_4px_14px_rgba(107,78,255,0.28)] hover:!border-[#5A3EEB] hover:!bg-[#5A3EEB] disabled:!border-[#C3C8D4] disabled:!bg-[#C3C8D4] disabled:shadow-none"
            }>
            {submitLabel}
          </Button>
        </div>
      </form>
    </div>
  );
};
